import React from 'react';
import {
  Users,
  FileText,
  Clock,
  DollarSign,
  TrendingUp,
  Calendar,
  Bell,
  LogOut,
  Settings,
  Shield
} from 'lucide-react';
import { useAuthStore } from '../auth/authStore';
import { useNavigate } from 'react-router-dom';
import PunchButton from '../attendance/PunchButton';
import toast from 'react-hot-toast';
import { NotificationBell } from '@/components/notifications/NotificationBell';

export const DashboardPage: React.FC = () => {
  const { user, tenant, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const navigateToTasks = () => {
    if (user?.roles?.includes('FIRM_ADMIN') || user?.roles?.includes('FirmAdmin')) {
      navigate('/firm-admin/tasks');
    } else if (user?.roles?.includes('Manager')) {
      navigate('/tasks/manager');
    } else if (user?.roles?.includes('Staff')) {
      navigate('/tasks/staff');
    } else {
      // Fallback for other roles
      navigate('/firm-admin/tasks');
    }
  };

  const navigateToClients = () => {
    if (user?.roles?.includes('FIRM_ADMIN') || user?.roles?.includes('FirmAdmin')) {
      navigate('/firm-admin/clients');
    }
  };

  const navigateToReports = () => {
    if (user?.roles?.includes('FIRM_ADMIN') || user?.roles?.includes('FirmAdmin')) {
      navigate('/firm-admin/reports');
    }
  };

  const navigateToSettings = () => {
    if (user?.roles?.includes('FIRM_ADMIN') || user?.roles?.includes('FirmAdmin')) {
      navigate('/firm-admin/settings');
    }
  };

  const stats = [
    {
      title: 'Active Clients',
      value: '24',
      change: '+12%',
      icon: Users,
      color: 'bg-primary-500',
    },
    {
      title: 'Pending Tasks',
      value: '18',
      change: '-5%',
      icon: FileText,
      color: 'bg-secondary-500',
    },
    {
      title: 'Hours This Week',
      value: '42.5',
      change: '+8%',
      icon: Clock,
      color: 'bg-pastel-purple',
    },
    {
      title: 'Revenue (MTD)',
      value: '$12,450',
      change: '+15%',
      icon: DollarSign,
      color: 'bg-pastel-orange',
    },
  ];

  const recentActivities = [
    {
      id: 1,
      type: 'task',
      message: 'T2 Corporate Return completed for ABC Corp',
      time: '2 hours ago',
      user: 'Sarah Johnson',
    },
    {
      id: 2,
      type: 'client',
      message: 'New client onboarded: XYZ Manufacturing',
      time: '4 hours ago',
      user: 'Mike Chen',
    },
    {
      id: 3,
      type: 'invoice',
      message: 'Invoice #INV-2024-001 sent to client',
      time: '6 hours ago',
      user: 'Admin User',
    },
    {
      id: 4,
      type: 'timesheet',
      message: 'Weekly timesheet submitted',
      time: '1 day ago',
      user: 'Jennifer Smith',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-soft border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-display font-bold text-primary-500">
                ERPCA
              </h1>
              <div className="ml-4 text-sm text-gray-500">
                {tenant?.name}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {user?.roles.includes('SuperAdmin') && (
                <button
                  onClick={() => navigate('/admin')}
                  className="p-2 text-purple-500 hover:text-purple-600 transition-colors"
                  title="SuperAdmin Dashboard"
                >
                  <Shield className="w-5 h-5" />
                </button>
              )}
              {user?.roles.includes('FirmAdmin') && (
                <button
                  onClick={() => navigate('/firm-admin')}
                  className="p-2 text-blue-500 hover:text-blue-600 transition-colors"
                  title="Firm Administration"
                >
                  <Settings className="w-5 h-5" />
                </button>
              )}
              <NotificationBell />
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <Settings className="w-5 h-5" />
              </button>

              {/* Punch Button - only show for non-admin users */}
              {!user?.roles.includes('SuperAdmin') && !user?.roles.includes('FirmAdmin') && (
                <PunchButton compact />
              )}

              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {user?.roles.join(', ')}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-2">
            Welcome back, {user?.firstName}!
          </h2>
          <p className="text-gray-600">
            {user?.roles?.includes('FIRM_ADMIN') || user?.roles?.includes('FirmAdmin')
              ? "Here's what's happening with your practice today."
              : "Here's your dashboard overview."
            }
          </p>
          {tenant && (
            <p className="text-sm text-primary-600 mt-2">
              {tenant.name} • {user?.roles?.join(', ')}
            </p>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stat.value}
                  </p>
                  <p className={`text-sm ${
                    stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change} from last month
                  </p>
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Activity */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Recent Activity
              </h3>
              <button className="text-sm text-primary-500 hover:text-primary-600 font-medium">
                View All
              </button>
            </div>
            
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 mb-1">
                      {activity.message}
                    </p>
                    <p className="text-xs text-gray-500">
                      {activity.time} • {activity.user}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Quick Actions
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              {(user?.roles?.includes('FIRM_ADMIN') || user?.roles?.includes('FirmAdmin') || user?.roles?.includes('Manager')) && (
                <button
                  onClick={navigateToClients}
                  className="p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 text-center"
                >
                  <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-600">
                    Manage Clients
                  </span>
                </button>
              )}

              <button
                onClick={navigateToTasks}
                className="p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 text-center"
              >
                <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-600">
                  {user?.roles?.includes('Manager') ? 'Manage Team Tasks' :
                   user?.roles?.includes('Staff') ? 'My Tasks' : 'View Tasks'}
                </span>
              </button>

              <button
                onClick={navigateToReports}
                className="p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 text-center"
              >
                <TrendingUp className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-600">
                  View Reports
                </span>
              </button>

              <button
                onClick={navigateToSettings}
                className="p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 text-center"
              >
                <Settings className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-600">
                  Settings
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-4 text-sm text-gray-500 bg-white rounded-full px-6 py-3 shadow-soft">
            <span className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              All Systems Operational
            </span>
            <span className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              Multi-tenant Ready
            </span>
          </div>
        </div>
      </main>
    </div>
  );
};
